import { computed, watch } from 'vue'
import {
  cleTranche,
  tranchesDeLaDuree,
  groupesNecessaires,
  palierApplicable,
  totalDeLaDemande,
  prixInterDeLaDuree,
  formulesDuDevis,
  venduParSessions,
  formulesEnSessions,
  libelleFormuleEnSessions,
  libelleSessions,
  estEnSessions,
  MENTION_RETIREE
} from '../../utils/formuleFormation.js'

/**
 * Choix d'une formule sur une demande : une durée, puis un effectif parmi ceux
 * vendus à cette durée. Le couple des deux désigne une formule du catalogue,
 * c'est-à-dire ce qui est réellement vendu.
 *
 * L'effectif se demande comme sur le formulaire public : combien de
 * personnes, en groupes de quelle taille, et combien de groupes. Les trois
 * décident du prix, et la fiche doit annoncer le même que celui vu par le
 * demandeur — les deux passent par les mêmes règles de tarif.
 *
 * Partagé par les deux fiches demande — l'actuelle et l'ancienne — qui
 * présentent ce bloc à l'identique.
 *
 * @param {Object} props les props du composant : `selectedFormation`,
 *   `editedQuote` et `quote`. On prend l'objet entier pour rester réactif.
 */
export function useSelectionFormule(props) {
  const formules = computed(() => formulesDuDevis(props.selectedFormation, props.quote))

  /**
   * Les durées proposées, sans doublon, dans l'ordre de la grille du catalogue.
   *
   * `valeur` reste la durée seule : c'est elle qui désigne une ligne de la
   * grille. `libelle` ajoute le nombre de jours quand la formation s'étale, ce
   * qu'une durée en heures ne laisse pas voir.
   */
  const dureesDisponibles = computed(() => {
    const parDuree = new Map()
    for (const formule of formules.value) {
      if (!parDuree.has(formule.duree)) {
        parDuree.set(formule.duree, { valeur: formule.duree, libelle: formule.dureeLibelle || formule.duree })
      }
    }
    return [...parDuree.values()].map((d) => (
      caseRetiree.value && d.valeur === formuleChoisie.value.duree
        ? { ...d, libelle: `${d.libelle} ${MENTION_RETIREE}` }
        : d
    ))
  })

/**
   * `null` posé par l'utilisateur et champ jamais renseigné sont deux choses
   * différentes : `??` les confondait, et vider la durée réaffichait aussitôt
   * la formule enregistrée — impossible de détacher une formule d'un devis.
   */
  const formuleRetenue = computed(() => (
    props.editedQuote && 'formation_formule_id' in props.editedQuote
      ? props.editedQuote.formation_formule_id
      : props.quote?.formation_formule_id
  ))

  const formuleChoisie = computed(() => {
    const id = Number(formuleRetenue.value)
    return formules.value.find((f) => f.id === id) || null
  })

  /** La case du devis a quitté la grille : on le dit là où elle s'affiche. */
  const caseRetiree = computed(() => Boolean(formuleChoisie.value?.retiree))

  const dureeChoisie = computed(() => formuleChoisie.value?.duree || '')

  const invitationTranche = computed(() => (
    dureeChoisie.value ? '-- Sélectionner une taille de groupe --' : "Choisissez d'abord une durée"
  ))

/**
 * Ce que coûte la demande. Même règle que le formulaire public, pour que la
 * fiche n'annonce pas un autre prix que celui vu par le demandeur : des
 * groupes en intra, un tarif par personne en inter.
 */
function totalDe(formule) {
  return totalDeLaDemande(formules.value, {
    lieu: lieuRetenu.value,
    dureeMinutes: dureeMinutesChoisie.value,
    palier: formule,
    effectif: effectifDemande.value,
    nbGroupes: nombreGroupes.value
  })
}

/** Où se tient la formation : c'est lui qui décide de la règle de tarif. */
  const lieuRetenu = computed(
    () => props.editedQuote?.formation_lieu || props.quote?.formation_lieu
  )

  /** L'intra se vend par groupe ; l'inter au stagiaire. */
  const enIntra = computed(() => (
    (props.editedQuote?.formation_lieu || props.quote?.formation_lieu) !== 'INTER'
  ))

  /** L'effectif annoncé sur la demande ne vit pas au même endroit selon le lieu. */
  const champEffectif = computed(() => (
    lieuRetenu.value === 'INTER'
      ? 'inter_nombre_participants'
      : 'intra_nombre_participants'
  ))

  const effectifDemande = computed(() => {
    const champ = champEffectif.value
    return Number(
      props.editedQuote && champ in props.editedQuote
        ? props.editedQuote[champ]
        : props.quote?.[champ]
    )
  })

  const dureeMinutesChoisie = computed(() => formuleChoisie.value?.dureeMinutes
    || formules.value.find((f) => f.duree === dureeChoisie.value)?.dureeMinutes
    || 0)

  /** Les tailles de groupe vendues à la durée retenue. */
  const tranchesDisponibles = computed(() => (
    tranchesDeLaDuree(formules.value, dureeMinutesChoisie.value).map((t) => (
      caseRetiree.value && t.cle === cleTranche(formuleChoisie.value) ? { ...t, retiree: true } : t
    ))
  ))

  const trancheChoisie = computed(() => (
    formuleChoisie.value ? cleTranche(formuleChoisie.value) : ''
  ))

  const nombreGroupes = computed(() => {
    const voulus = Number(
      props.editedQuote && 'nombre_groupes' in props.editedQuote
        ? props.editedQuote.nombre_groupes
        : props.quote?.nombre_groupes
    )
    return Number.isFinite(voulus) && voulus > 0 ? voulus : 1
  })

  /** Le palier facturé, une fois la taille et le nombre de groupes connus. */
  const palierRetenu = computed(() => palierApplicable(formules.value, {
    dureeMinutes: dureeMinutesChoisie.value,
    tranche: trancheChoisie.value,
    nbGroupes: nombreGroupes.value
  }))

  /** Le nombre de groupes qu'appelle l'effectif, pour le proposer. */
  function groupesConseilles(tranche) {
    const taille = tranchesDisponibles.value.find((t) => t.cle === tranche)?.max
    return groupesNecessaires(effectifDemande.value, taille)
  }

  /**
   * L'effectif recale le nombre de groupes, comme sur le formulaire public.
   *
   * Sans cela, relever l'effectif d'une demande laissait le devis au tarif
   * d'un seul groupe : le champ affichait « il en faut 2 » pendant que le
   * montant en facturait un. Le recalcul passe par `choisirNombreGroupes`,
   * qui relit le palier et respecte un montant negocie.
   *
   * Ne s'applique qu'en edition : hors edition il n'y a pas d'`editedQuote` a
   * ecrire, et la fiche doit montrer ce qui est enregistre, pas un recalcul.
   */
  watch(effectifDemande, () => {
    // « Par journée », une formule est un forfait à un seul groupe : l'effectif
    // ne la découpe pas, `alerteEffectif` dit seulement s'il la dépasse.
    if (parJournee.value) return
    if (!props.isEditing || !props.editedQuote || !trancheChoisie.value) return

    const conseil = groupesConseilles(trancheChoisie.value)
    if (conseil === nombreGroupes.value) return

    choisirNombreGroupes(conseil)
  })

  /**
   * Choix d'une taille de groupe : elle désigne un palier, et le nombre de
   * groupes se recale sur l'effectif annoncé — sinon un devis pour quarante
   * personnes resterait au tarif d'un seul groupe.
   */
  function choisirTranche(tranche) {
    // Le verdict se prend avant de toucher au nombre de groupes : sinon le
    // montant, compare a un total qui vient de changer, passerait pour
    // negocie a chaque fois et ne suivrait plus jamais la grille.
    const negocie = montantNegocie()
    if (!tranche) {
      retenirFormule(null, negocie)
      return
    }
    props.editedQuote.nombre_groupes = groupesConseilles(tranche)
    retenirFormule(palierApplicable(formules.value, {
      dureeMinutes: dureeMinutesChoisie.value,
      tranche,
      nbGroupes: props.editedQuote.nombre_groupes
    }), negocie)
  }

  function choisirNombreGroupes(valeur) {
    const negocie = montantNegocie()
    const voulus = Number(valeur)
    props.editedQuote.nombre_groupes = Number.isFinite(voulus) && voulus > 0 ? voulus : null
    retenirFormule(palierApplicable(formules.value, {
      dureeMinutes: dureeMinutesChoisie.value,
      tranche: trancheChoisie.value,
      nbGroupes: props.editedQuote.nombre_groupes
    }), negocie)
  }

/**
   * Le montant du devis a-t-il été négocié à la main ?
   *
   * On le déduit plutôt que de le stocker : tant qu'il vaut encore le prix de
   * la formule retenue — ou rien du tout — personne n'y a touché, et le suivre
   * est sans risque. Dès qu'il s'en écarte, c'est une remise ou un forfait
   * déplacement, et l'écraser ferait perdre une négociation.
   */
  function montantNegocie() {
    const montant = Number(props.editedQuote?.quote_amount)
    if (!Number.isFinite(montant) || montant <= 0) return false

    const total = totalDe(formuleChoisie.value)

    /*
     * UN DEVIS SANS FORMULE PORTE UN MONTANT QUE PERSONNE NE PEUT EXPLIQUER,
     * ET C’EST PRÉCISÉMENT POUR ÇA QU’IL FAUT LE GARDER. Sur un devis d’avant
     * les formules, `formuleChoisie` est nul : `totalDe` rendait `null`, la
     * fonction concluait « pas négocié », et choisir une durée — ne serait-ce
     * que pour que la convention imprime la bonne — écrasait 450 EUR négociés
     * par les 640 du barème. Sans un mot, et sans que le rappel de prix ne
     * s’affiche, puisque les deux coïncidaient désormais.
     */
    if (total === null) return true

    return montant !== total
  }

  /**
   * Retient une formule, et aligne le montant du devis dessus quand il n'a pas
   * été négocié.
   *
   * Une formation au tarif sur mesure n'a de prix dans aucune de ses formules :
   * elle sort par le test sur `prix`, sans avoir besoin d'un cas à part.
   */
  function retenirFormule(formule, negocie = montantNegocie()) {
    props.editedQuote.formation_formule_id = formule?.id ?? null

    if (!formule || negocie) return

    const total = totalDe(formule)
    if (total === null) return

    props.editedQuote.quote_amount = total
  }

  /**
   * Changer de durée garde l'effectif si la nouvelle durée le propose aussi,
   * sinon retombe sur le premier — sans quoi il faudrait ressaisir les deux à
   * chaque essai. Une durée vidée détache la formule.
   */
  function choisirDuree(duree) {
    if (!duree) {
      retenirFormule(null)
      return
    }

    // Le repli ne retient qu'un tarif à UN groupe : les paliers d'une même
    // case partagent leur ordre en base, rien ne garantit lequel sort en
    // premier, et prendre celui à quatre groupes poserait 490 € là où la
    // formation se vend 640 €.
    const candidates = formules.value.filter(
      (f) => f.duree === duree && (f.nbGroupes || 1) === 1
    )

    // La tranche s'identifie par ses bornes, pas par son libellé : celui-ci
    // porte désormais « × 3 groupes » et ne désigne plus la seule tranche.
    const attendue = formuleChoisie.value ? cleTranche(formuleChoisie.value) : ''
    const memeEffectif = candidates.find((f) => cleTranche(f) === attendue)
    retenirFormule(memeEffectif || candidates[0] || null)
  }

  /**
   * Rappel du tarif catalogue quand le montant du devis s'en écarte. Le
   * commercial doit voir l'écart qu'il a créé — ou hérité d'une formule
   * changée après coup — sans qu'on lui réécrive sa négociation.
   */
  const rappelPrix = computed(() => {
    const formule = formuleChoisie.value
    const total = totalDe(formule)
    if (total === null) return ''

    const montant = Number(props.editedQuote?.quote_amount)
    if (!Number.isFinite(montant) || montant <= 0 || montant === total) return ''

    // Sur un tarif dégressif, le total seul ne se vérifie pas : on montre le
    // calcul, sinon le commercial ne peut pas dire d'où sort le chiffre. En
    // inter le multiplicateur est l'effectif, pas les groupes.
    const inter = lieuRetenu.value === 'INTER'
    const facteur = inter ? effectifDemande.value : nombreGroupes.value
    const unitaire = inter
      ? prixInterDeLaDuree(formules.value, dureeMinutesChoisie.value)
      : formule.prix
    const detail = facteur > 1 ? ` (${facteur} × ${unitaire} €)` : ''
    return `Cette formule est au tarif de ${total} €${detail} ; le montant du devis est à ${montant} €.`
  })

  /**
   * « PAR JOURNÉE », LA FORMULE SE CHOISIT D'UN COUP. Chaque ligne du catalogue
   * est un forfait complet — un format, une capacité, ses sessions, son
   * tarif — que durée, taille et nombre de groupes ne décrivent pas : la fiche
   * pose une seule liste à la place des trois.
   */
  const parJournee = computed(() => venduParSessions(formules.value))

  const formulesJournee = computed(() => formulesEnSessions(formules.value).map((f) => ({
    id: f.id,
    libelle: [
      libelleFormuleEnSessions(f),
      libelleSessions(f.nbSessions),
      f.prix === null || f.prix === undefined ? null : `${f.prix} €`
    ].filter(Boolean).join(' — ') + (f.retiree ? ` ${MENTION_RETIREE}` : '')
  })))

  const formuleJourneeChoisie = computed(() => formuleChoisie.value?.id ?? '')

  /** Ce que la fiche affiche en lecture : la formule retenue, en toutes lettres. */
  const libelleFormuleJournee = computed(
    () => formulesJournee.value.find((f) => f.id === formuleJourneeChoisie.value)?.libelle || ''
  )

  function choisirFormuleJournee(id) {
    const negocie = montantNegocie()
    const formule = formules.value.find((f) => f.id === Number(id)) || null
    props.editedQuote.nombre_groupes = formule ? 1 : null
    retenirFormule(formule, negocie)
  }

  /** En lecture, la formule du devis prime sur la durée historique du catalogue. */
  const mention = computed(() => (caseRetiree.value ? ` ${MENTION_RETIREE}` : ''))
  const dureeAffichee = computed(() => {
    const duree = formuleChoisie.value?.dureeLibelle || formuleChoisie.value?.duree
    return duree ? `${duree}${mention.value}` : props.selectedFormation?.DUREE
  })
  const effectifAffiche = computed(() => {
    const effectif = formuleChoisie.value?.participants
    return effectif ? `${effectif}${mention.value}` : props.selectedFormation?.NOMBRE
  })

  /**
   * Le groupe annoncé sur la demande sort-il de la formule retenue ? Le
   * commercial doit le voir avant d'envoyer un prix qui ne couvre pas tout le
   * monde.
   */
  const alerteEffectif = computed(() => {
    const formule = formuleChoisie.value
    // Le nombre de participants ne vit pas au même endroit selon le lieu : la
    // demande porte l'un ou l'autre, jamais les deux.
    const demandes = effectifDemande.value
    if (!formule || !Number.isFinite(demandes) || demandes <= 0) return ''

    // Trois groupes de douze accueillent trente-six personnes : la fourchette
    // se lit par groupe, la capacité se compte sur l'ensemble. En inter il n'y
    // a pas de groupes : la fourchette de la formule vaut telle quelle.
    const groupes = lieuRetenu.value === 'INTER' ? 1 : nombreGroupes.value
    const mini = formule.participantsMin * groupes
    const maxi = formule.participantsMax * groupes
    if (demandes >= mini && demandes <= maxi) return ''

    if (estEnSessions(formule)) {
      return `La demande porte sur ${demandes} participants, au-delà des ${maxi} personnes de cette formule.`
    }
    const capacite = groupes > 1 ? `${mini} à ${maxi}` : formule.participants
    return `La demande porte sur ${demandes} participants, hors de la fourchette ${capacite} de cette formule.`
  })

  return {
    parJournee,
    formulesJournee,
    formuleJourneeChoisie,
    libelleFormuleJournee,
    choisirFormuleJournee,
    dureesDisponibles,
    tranchesDisponibles,
    trancheChoisie,
    choisirTranche,
    nombreGroupes,
    choisirNombreGroupes,
    groupesConseilles,
    palierRetenu,
    effectifDemande,
    champEffectif,
    enIntra,
    dureeChoisie,
    invitationTranche,
    choisirDuree,
    rappelPrix,
    dureeAffichee,
    effectifAffiche,
    alerteEffectif
  }
}
